import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, User, Mail, Shield, CheckCircle, XCircle } from 'lucide-react';
import { LogoLoader } from '@/components/ui/logo-loader';
import { apiRequest } from '@/lib/queryClient';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/hooks/use-alert';
import { API_ROUTES } from '@mangelapp/shared';

interface InviteData {
  email: string;
  role: string;
  organizationName: string;
  organizationId: string;
  expiresAt: string;
  userExists?: boolean;
  existingUserData?: { firstName?: string; lastName?: string; displayName?: string } | null;
}

export default function InviteAcceptPage() {
  const { t } = useTranslation(['common', 'pages.inviteAccept']);
  const alert = useAlert();
  const [, setLocation] = useLocation();
  const [token, setToken] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) setToken(tokenParam);
  }, []);

  const { data: inviteData, isLoading: verifying, error: verifyError } = useQuery({
    queryKey: ['/api/auth/invite', token],
    queryFn: async () => {
      const url = `/api/auth/invite/${encodeURIComponent(token)}`;
      const res = await apiRequest(url);
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  const invite: InviteData | null = inviteData?.data || null;
  const isExistingUser = !!invite?.userExists;

  const acceptInviteMutation = useMutation({
    mutationFn: async () => {
      const body: { token: string; password?: string; firstName?: string; lastName?: string } = { token };
      if (!isExistingUser) {
        body.password = password;
        body.firstName = firstName;
        body.lastName = lastName;
      }
      const res = await apiRequest(API_ROUTES.AUTH.ACCEPT_INVITE, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return await res.json();
    },
    onSuccess: () => {
      setLocation('/login?message=invite-accepted');
    },
    onError: (error: any) => {
      setErrors({ submit: error.message || t('pages.inviteAccept:errors.acceptFailed') });
    },
  });

  const rejectInviteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(API_ROUTES.AUTH.REJECT_INVITE, {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      return await res.json();
    },
    onSuccess: () => {
      setLocation('/login?message=invite-rejected');
    },
    onError: (error: any) => {
      setErrors({ submit: error.message || t('pages.inviteAccept:errors.rejectFailed') });
    },
  });

  const validateForm = () => {
    if (isExistingUser) return true;
    const newErrors: Record<string, string> = {};
    if (!password) {
      newErrors.password = t('pages.inviteAccept:validation.passwordRequired');
    } else if (password.length < 6) {
      newErrors.password = t('pages.inviteAccept:validation.passwordMin');
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('pages.inviteAccept:validation.passwordMismatch');
    }
    if (!firstName.trim()) newErrors.firstName = t('pages.inviteAccept:validation.firstNameRequired');
    if (!lastName.trim()) newErrors.lastName = t('pages.inviteAccept:validation.lastNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) acceptInviteMutation.mutate();
  };

  const handleReject = async () => {
    const confirmed = await alert.confirm({
      message: t('pages.inviteAccept:rejectConfirm'),
      variant: 'destructive',
      confirmText: t('pages.inviteAccept:reject'),
      cancelText: t('common:cancel'),
    });
    if (confirmed) rejectInviteMutation.mutate();
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <LogoLoader size="md" />
            <p className="text-gray-600">{t('pages.inviteAccept:verifying')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (verifyError || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardHeader className="text-center">
            <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <CardTitle className="text-red-600">{t('pages.inviteAccept:invalidTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">{t('pages.inviteAccept:invalidMessage')}</p>
            <Button onClick={() => setLocation('/login')}>{t('pages.inviteAccept:goToLogin')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Building2 className="w-12 h-12 mx-auto text-blue-600" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {isExistingUser ? t('pages.inviteAccept:titleAccept') : t('pages.inviteAccept:titleCreate')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('pages.inviteAccept:invitedTo')} <strong>{invite.organizationName}</strong>
          </p>
        </div>

        {errors.submit && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{errors.submit}</AlertDescription>
          </Alert>
        )}

        {isExistingUser ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {t('pages.inviteAccept:role')}: {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">{t('pages.inviteAccept:existingUserIntro')}</p>
              <div className="flex gap-3">
                <Button
                  className="flex-1"
                  onClick={() => acceptInviteMutation.mutate()}
                  disabled={acceptInviteMutation.isPending || rejectInviteMutation.isPending}
                >
                  {acceptInviteMutation.isPending ? <LogoLoader size="sm" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {t('pages.inviteAccept:accept')}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleReject}
                  disabled={acceptInviteMutation.isPending || rejectInviteMutation.isPending}
                >
                  {rejectInviteMutation.isPending ? <LogoLoader size="sm" /> : <XCircle className="w-4 h-4 mr-2" />}
                  {t('pages.inviteAccept:reject')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('pages.inviteAccept:createAccount')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">{t('pages.inviteAccept:email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input id="email" type="email" value={invite.email} disabled className="pl-10 bg-gray-50" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="role">{t('pages.inviteAccept:role')}</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input id="role" value={invite.role.charAt(0).toUpperCase() + invite.role.slice(1)} disabled className="pl-10 bg-gray-50" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="firstName">{t('pages.inviteAccept:firstName')} *</Label>
                  <Input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={errors.firstName ? 'border-red-500' : ''} />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName">{t('pages.inviteAccept:lastName')} *</Label>
                  <Input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className={errors.lastName ? 'border-red-500' : ''} />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <Label htmlFor="password">{t('pages.inviteAccept:password')} *</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={errors.password ? 'border-red-500' : ''} />
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                </div>
                <div>
                  <Label htmlFor="confirmPassword">{t('pages.inviteAccept:confirmPassword')} *</Label>
                  <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={errors.confirmPassword ? 'border-red-500' : ''} />
                  {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={acceptInviteMutation.isPending}>
                  {acceptInviteMutation.isPending ? <><LogoLoader size="sm" /> {t('pages.inviteAccept:creating')}</> : <><CheckCircle className="w-4 h-4 mr-2" /> {t('pages.inviteAccept:accept')}</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {!isExistingUser && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t('pages.inviteAccept:alreadyHaveAccount')}{' '}
              <button type="button" onClick={() => setLocation('/login')} className="text-blue-600 hover:text-blue-500 font-medium">
                {t('pages.inviteAccept:signInHere')}
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
